


// Channel-aware compose/review card for the Communication (COM) module. Used both in the SAM
// workspace/card area (DRAFT_COMMUNICATION actions render it as a `draftcard` surface — side panel
// on desktop, card/UI mode on mobile, single page in hands-free) and on the Communications page
// (blank drafts + history detail). Review-before-send: the Send button here is the ONLY way a draft
// goes out. Edits made here are persisted live (debounced PATCH) and surfaced to SAM so the user can
// edit in the UI or ask SAM to change the draft ("change the subject"), both reflected in place.
//
// Modes:
//  • draft        — editable recipients/subject/body/attachments + Discard/Send footer
//  • sent (comm)  — read-only body + status chip (+ failed banner/Retry), live-updated via the
//                   `communication:sent` / `communication:failed` socket events
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { socketService } from '@/services/SocketService';
import { apiErrorMessage } from '@/utils/apiError';
import { cn } from '@/utils/cn';
import { Button } from '@/components/UI/Button';
import { Spinner } from '@/components/UI/Spinner';
import { useListTenantUsersQuery, type TenantUser } from '@/redux/internal-services/onboardingApi';
import {
  useDiscardDraftCommunicationMutation,
  useGetCommunicationByIdQuery,
  useRetryCommunicationMutation,
  useSendDraftCommunicationMutation,
  useSendEmailMutation,
  useSendInAppMessageMutation,
  useSendSmsMutation,
  useUpdateDraftCommunicationMutation,
  useUploadCommunicationAttachmentMutation,
  type CommunicationAttachment,
  type CommunicationChannel,
  type CommunicationOut,
  type CommunicationStatus,
  type ContextLink,
  type RecipientIn,
  type SendDraftCommunicationInput,
  type UpdateDraftCommunicationInput,
} from '@/redux/internal-services/workspaceApiExtensions';

// ─── message-kind for the workspace card area ─────────────────────────────────

/** Seed for a draft — what a SAM DRAFT_COMMUNICATION action (or a blank page tab) provides. */
export interface ComposeDraftInput {
  channel: CommunicationChannel;
  recipients?: RecipientIn[];
  subject?: string;
  body?: string;
  context?: ContextLink | null;
}

/** Live snapshot of the draft's editable fields, pushed to SAM (both agents) so voice/text stay
 * aware of edits made in the UI. Matches the backend `active_draft` context shape. */
export interface DraftSnapshot {
  communication_id?: string;
  channel: CommunicationChannel;
  subject?: string;
  body?: string;
  recipients: Array<{ name?: string; email?: string; phone?: string; user_id?: string }>;
}

/** A draft rendered in the workspace card area (kind: 'draftcard'). Persisted SAM drafts carry
 * `communication_id` (server-side record); `draft` is the ephemeral fallback / live re-seed payload.
 * `rev` is bumped each time SAM re-publishes the draft (a live edit) so the card re-seeds from
 * `draft`; `messageId` links it to the assistant reply of the turn that produced it. */
export interface DraftCardMessage {
  kind: 'draftcard';
  id: string;
  draft?: ComposeDraftInput;
  communication_id?: string;
  rev?: number;
  messageId?: string;
}

// ─── constants / helpers ───────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const SMS_SEGMENT = 160;

const CHANNEL_TAG: Record<CommunicationChannel, string> = {
  email: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  sms: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  in_app: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};
const CHANNEL_NAME: Record<CommunicationChannel, string> = { email: 'EMAIL', sms: 'SMS', in_app: 'IN-APP' };

const PILL: Record<string, { cls: string; dot: string }> = {
  draft:     { cls: 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300', dot: 'bg-gray-400' },
  pending:   { cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400', dot: 'bg-amber-500' },
  queued:    { cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400', dot: 'bg-amber-500' },
  sent:      { cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', dot: 'bg-blue-500' },
  delivered: { cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', dot: 'bg-green-500' },
  failed:    { cls: 'bg-red-100 text-[#CF4148] dark:bg-red-900/30 dark:text-red-400', dot: 'bg-[#CF4148]' },
  received:  { cls: 'bg-ui-subtle text-ui-text2', dot: 'bg-ui-text3' },
};

type DraftRecipient = RecipientIn & { key: string };

let recipientSeq = 0;
const newKey = () => `r-${Date.now()}-${recipientSeq++}`;

const digitsOf = (s: string) => s.replace(/\D/g, '');

function formatBytes(bytes?: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Shape of the `communication:sent` / `communication:failed` socket payloads. */
interface CommSocketEvent {
  communication_id?: string;
  type?: string;
  status?: string;
  error_detail?: string | null;
  recipients?: Array<{ name?: string | null; status?: string; error?: string | null }>;
}

// ─── tiny presentational bits ──────────────────────────────────────────────────

const ChannelTag = ({ channel }: { channel: CommunicationChannel }) => (
  <span className={cn('inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide', CHANNEL_TAG[channel])}>
    {CHANNEL_NAME[channel]}
  </span>
);

const StatusPill = ({ status }: { status: string }) => {
  const p = PILL[status] ?? PILL.draft;
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide', p.cls)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', p.dot)} />
      {status}
    </span>
  );
};

// ─── recipient picker (searchable dropdown → tenant users) ─────────────────────
// in_app: pick tenant users only. email / sms: pick a tenant user that HAS the needed contact
// (email for email, phone for sms — shown as name + contact) OR type a free external address/number.

const RecipientPickerInput = ({ channel, onAddUser, onAddFreeText, excludeIds }: {
  channel: CommunicationChannel;
  onAddUser: (u: TenantUser) => void;
  onAddFreeText: (value: string) => void;
  excludeIds: Set<string>;
}) => {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const { data, isFetching } = useListTenantUsersQuery(
    { search: q.trim() || undefined, page_size: 20 },
    { skip: !open },
  );
  const contactOf = (u: TenantUser) => (channel === 'sms' ? u.phone : u.email);
  // Only surface tenant users who actually have the contact this channel needs.
  const users = (data?.data ?? [])
    .filter((u) => !excludeIds.has(u.user_id))
    .filter((u) => channel === 'in_app' || !!contactOf(u));

  // Commit a typed external address/number (email & sms only). Ignore plain text (a name typed to
  // search) so it isn't added as a bogus recipient — the user should pick that from the dropdown.
  const commitFreeText = () => {
    const v = q.trim();
    if (!v || channel === 'in_app') return;
    const ok = channel === 'email' ? EMAIL_RE.test(v) : digitsOf(v).length >= 7;
    if (ok) { onAddFreeText(v); setQ(''); }
  };

  const placeholder = channel === 'in_app'
    ? t('communications.addUser', '+ add user')
    : channel === 'sms'
      ? t('communications.addSmsRecipient', '+ add number or user')
      : t('communications.addEmailRecipient', '+ add email or user');

  return (
    <div className="relative">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Enter' && channel !== 'in_app') { e.preventDefault(); commitFreeText(); } }}
        onBlur={() => { setTimeout(() => setOpen(false), 150); commitFreeText(); }}
        placeholder={placeholder}
        className="h-8 w-44 rounded-lg border border-dashed border-ui-border bg-transparent px-2 text-xs text-ui-text placeholder:text-ui-text3 focus:border-brand-dark focus:outline-none"
      />
      {open && (
        <div className="absolute left-0 top-9 z-30 max-h-56 w-64 overflow-y-auto rounded-lg border border-ui-border bg-ui-card shadow-lg">
          {isFetching ? (
            <div className="flex justify-center py-3"><Spinner size="sm" /></div>
          ) : users.length === 0 ? (
            <p className="px-3 py-2 text-xs text-ui-text3">
              {channel === 'sms'
                ? t('communications.noUsersWithPhone', 'No users with a phone number.')
                : t('communications.noUsers', 'No matching users.')}
            </p>
          ) : (
            users.map((u) => (
              <button
                key={u.user_id}
                onMouseDown={(e) => { e.preventDefault(); onAddUser(u); setQ(''); }}
                className="block w-full px-3 py-2 text-left hover:bg-ui-hover"
              >
                <span className="block text-xs font-medium text-ui-text">{u.name || contactOf(u)}</span>
                {contactOf(u) && <span className="block text-[11px] text-ui-text3">{contactOf(u)}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ─── component ─────────────────────────────────────────────────────────────────

interface ComposeCardProps {
  /** Draft seed (SAM action or a blank page tab). Ignored when `communication` is given. */
  draft?: ComposeDraftInput;
  /** Server-side communication record id (persisted SAM draft) — the card loads the record
   * and renders it editable while status is 'draft', read-only otherwise. */
  communicationId?: string;
  /** An already-sent communication — renders the read-only review/status view. */
  communication?: CommunicationOut;
  /** Remove the card (chat thread) / reset the draft (page). Only offered while draft. */
  onDiscard?: () => void;
  /** Fired with the created communication after a successful send. */
  onSent?: (comm: CommunicationOut) => void;
  /** Always render the full card (legacy prop — the card is always full now; kept for callers). */
  fullView?: boolean;
  /** Bumped when SAM live-edits this draft — re-seed the editable fields from `draft`. */
  seedRev?: number;
  /** Fired (debounced) with the current editable fields whenever the USER edits, so the caller can
   * surface the live draft state to SAM. Null when the draft is sent/discarded. */
  onDraftEdit?: (snapshot: DraftSnapshot | null) => void;
  /** Reports the resolved channel + status so a host (e.g. DraftPanel) can title its header
   * ("Email draft" → "Email" once sent) — including for records loaded by id. */
  onMeta?: (meta: { channel: CommunicationChannel; status: string; isDraft: boolean }) => void;
  className?: string;
}

const ComposeCard: React.FC<ComposeCardProps> = ({ draft, communicationId, communication, onDiscard, onSent, seedRev, onDraftEdit, onMeta, className }) => {
  const { t } = useTranslation();

  // ── persisted-record load (SAM drafts survive reload through this) ──────────
  const { data: fetched, isLoading: loadingComm } = useGetCommunicationByIdQuery(communicationId as string, {
    skip: !communicationId || !!communication,
  });

  const [fetchedDraft, setFetchedDraft] = useState<CommunicationOut | null>(null);
  const channel: CommunicationChannel =
    communication?.communication_type ?? fetchedDraft?.communication_type ?? fetched?.communication_type ?? draft?.channel ?? 'email';

  // ── editable draft state ─────────────────────────────────────────────────────
  const [recipients, setRecipients] = useState<DraftRecipient[]>(
    () => (draft?.recipients ?? []).map((r) => ({ ...r, key: newKey() })),
  );
  const [subject, setSubject] = useState(draft?.subject ?? '');
  const [body, setBody] = useState(draft?.body ?? '');
  const [attachments, setAttachments] = useState<CommunicationAttachment[]>([]);
  const [discarded, setDiscarded] = useState(false);

  // ── sent communication state (live-merged from socket events) ────────────────
  const [comm, setComm] = useState<CommunicationOut | null>(communication ?? null);
  useEffect(() => { if (communication) setComm(communication); }, [communication]);

  // Sync from the fetched record. Draft fields seed ONCE (don't clobber in-progress edits
  // on refetch); a non-draft status always wins — the agent can send the draft on the
  // user's spoken/typed "send it", and the tag invalidation refetch flips this card to
  // the sent view.
  const seededRef = useRef(false);
  // Guards for the live-edit sync effect: syncReadyRef skips the seed cycles (initial / fetched /
  // SAM re-seed) so only genuine user edits push; lastSyncSigRef dedups identical field states.
  const syncReadyRef = useRef(false);
  const lastSyncSigRef = useRef('');
  const lastSeedRevRef = useRef<number | undefined>(seedRev);
  useEffect(() => {
    if (!fetched) return;
    if (fetched.status === 'discarded') {
      seededRef.current = true;
      setFetchedDraft(null);
      setDiscarded(true);
      return;
    }
    if (fetched.status !== 'draft') {
      seededRef.current = true;
      setFetchedDraft(null);
      setComm(fetched);
      return;
    }
    if (seededRef.current) return;
    seededRef.current = true;
    syncReadyRef.current = false;  // this seed must not be pushed back as a user edit
    setFetchedDraft(fetched);
    setRecipients((fetched.recipients ?? []).map((r) => ({
      recipient_type: r.recipient_type, user_id: r.user_id ?? undefined, contact_id: r.contact_id ?? undefined,
      name: r.name ?? undefined, email: r.email ?? undefined, phone: r.phone ?? undefined, key: newKey(),
    })));
    setSubject(fetched.subject ?? '');
    setBody(fetched.body ?? '');
    setAttachments(fetched.attachments ?? []);
  }, [fetched]);

  // SAM live-edited the draft (update_draft_communication re-publishes DRAFT_COMMUNICATION with a
  // bumped rev). Re-seed the editable fields from the fresh payload — the fetched record is stale
  // until refetch, so the action payload is authoritative. User-typed edits (no rev change) are
  // left untouched.
  useEffect(() => {
    if (seedRev === undefined || lastSeedRevRef.current === seedRev) return;
    lastSeedRevRef.current = seedRev;
    if (!draft) return;
    syncReadyRef.current = false;  // treat the re-seed as a seed, not a user edit
    setRecipients((draft.recipients ?? []).map((r) => ({ ...r, key: newKey() })));
    setSubject(draft.subject ?? '');
    setBody(draft.body ?? '');
  }, [seedRev, draft]);

  const draftId = fetchedDraft?.communication_id ?? null;
  const context = communication?.context ?? comm?.context ?? fetchedDraft?.context ?? draft?.context ?? null;

  const [validationError, setValidationError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const [sendEmail, { isLoading: sendingEmail }] = useSendEmailMutation();
  const [sendSms, { isLoading: sendingSms }] = useSendSmsMutation();
  const [sendInApp, { isLoading: sendingInApp }] = useSendInAppMessageMutation();
  const [sendDraft, { isLoading: sendingDraft }] = useSendDraftCommunicationMutation();
  const [updateDraft] = useUpdateDraftCommunicationMutation();
  const [discardDraft, { isLoading: discarding }] = useDiscardDraftCommunicationMutation();
  const [retryComm, { isLoading: retrying }] = useRetryCommunicationMutation();
  const [uploadAttachment, { isLoading: uploading }] = useUploadCommunicationAttachmentMutation();
  const sending = sendingEmail || sendingSms || sendingInApp || sendingDraft;

  const fileRef = useRef<HTMLInputElement>(null);

  const isDraft = !comm;
  const status = comm?.status ?? 'draft';

  // Report channel + status up so a host panel can title its header (and reflect send/discard).
  useEffect(() => { onMeta?.({ channel, status, isDraft }); }, [channel, status, isDraft, onMeta]);

  // ── live per-card status updates ────────────────────────────────────────────
  const commId = comm?.communication_id;
  useEffect(() => {
    if (!commId) return;
    const handler = (raw: unknown) => {
      const d = raw as CommSocketEvent;
      if (d?.communication_id !== commId) return;
      setComm((prev) => {
        if (!prev) return prev;
        const updates = d.recipients ?? [];
        return {
          ...prev,
          status: (d.status as CommunicationStatus) ?? prev.status,
          error_detail: d.error_detail ?? prev.error_detail,
          recipients: prev.recipients.map((r) => {
            const u = updates.find((x) => x.name && x.name === r.name);
            return u ? { ...r, status: (u.status as CommunicationStatus) ?? r.status, error: u.error ?? r.error } : r;
          }),
        };
      });
    };
    socketService.on('communication:sent', handler);
    socketService.on('communication:failed', handler);
    return () => {
      socketService.off('communication:sent', handler);
      socketService.off('communication:failed', handler);
    };
  }, [commId]);

  // ── live-edit sync: persist UI edits + surface them to SAM ────────────────────
  // Debounced. Persists the draft record (single source of truth, so a voice "send it" sends the
  // edited version) and pushes a snapshot up so SAM (text + voice) stays aware of what the user
  // changed. Skips seed cycles so only genuine user edits fire.
  useEffect(() => {
    if (!isDraft) return;
    const snap: DraftSnapshot = {
      communication_id: communicationId,
      channel,
      subject: channel === 'email' ? subject : undefined,
      body,
      recipients: recipients.map((r) => ({ name: r.name, email: r.email, phone: r.phone, user_id: r.user_id })),
    };
    const sig = JSON.stringify(snap);
    if (sig === lastSyncSigRef.current) return;
    lastSyncSigRef.current = sig;
    if (!syncReadyRef.current) { syncReadyRef.current = true; return; }
    const timer = setTimeout(() => {
      if (communicationId) {
        const edits: UpdateDraftCommunicationInput = channel === 'in_app'
          ? { recipient_user_ids: recipients.map((r) => r.user_id).filter(Boolean) as string[], body }
          : { recipients: recipients.map(({ key: _key, ...r }) => r), body, ...(channel === 'email' ? { subject } : {}) };
        updateDraft({ id: communicationId, body: edits }).unwrap().catch(() => { /* transient — retried on next edit */ });
      }
      onDraftEdit?.(snap);
    }, 600);
    return () => clearTimeout(timer);
  }, [isDraft, channel, subject, body, recipients, communicationId, onDraftEdit, updateDraft]);

  // ── recipients ───────────────────────────────────────────────────────────────
  const removeRecipient = (key: string) => setRecipients((prev) => prev.filter((r) => r.key !== key));

  const addFreeText = (value: string) => {
    const v = value.trim();
    if (!v) return;
    if (channel === 'email') setRecipients((prev) => [...prev, { key: newKey(), recipient_type: 'external', email: v }]);
    else setRecipients((prev) => [...prev, { key: newKey(), recipient_type: 'external', phone: v }]);
    setValidationError(null);
  };

  const addUser = (u: TenantUser) => {
    setRecipients((prev) => [...prev, {
      key: newKey(), recipient_type: 'user', user_id: u.user_id,
      name: u.name ?? undefined, email: u.email ?? undefined, phone: u.phone ?? undefined,
    }]);
    setValidationError(null);
  };

  const userIds = useMemo(() => new Set(recipients.map((r) => r.user_id).filter(Boolean) as string[]), [recipients]);

  // ── validation + send ────────────────────────────────────────────────────────
  const validate = (): string | null => {
    if (!recipients.length) return t('communications.errNoRecipients', 'Add at least one recipient.');
    if (channel === 'email') {
      const bad = recipients.find((r) => !r.email || !EMAIL_RE.test(r.email));
      if (bad) return t('communications.errBadEmail', 'Invalid email address: {{value}}', { value: bad.email || bad.name || '' });
    }
    if (channel === 'sms') {
      const bad = recipients.find((r) => { const n = digitsOf(r.phone || '').length; return n < 10 || n > 15; });
      if (bad) return t('communications.errBadPhone', 'Invalid phone number: {{value}}', { value: bad.phone || bad.name || '' });
    }
    if (channel === 'in_app' && recipients.some((r) => !r.user_id)) {
      return t('communications.errNotUser', 'In-app messages can only go to workspace users.');
    }
    if (channel === 'email' && !subject.trim()) return t('communications.errNoSubject', 'Subject is required.');
    if (!body.trim()) return t('communications.errNoBody', 'Message body is required.');
    return null;
  };

  const doSend = async () => {
    setSendError(null);
    const err = validate();
    setValidationError(err);
    if (err) return;
    const outbound: RecipientIn[] = recipients.map(({ key: _key, ...r }) => r);
    try {
      let result: CommunicationOut;
      if (draftId) {
        // Persisted SAM draft — explicit user send of the reviewed/edited draft.
        const edits: SendDraftCommunicationInput = channel === 'in_app'
          ? {
              recipient_user_ids: recipients.map((r) => r.user_id).filter(Boolean) as string[],
              body, context: context ?? undefined,
            }
          : {
              recipients: outbound, body, context: context ?? undefined,
              ...(channel === 'email'
                ? { subject: subject.trim(), attachments: attachments.length ? attachments : undefined }
                : {}),
            };
        result = await sendDraft({ id: draftId, body: edits }).unwrap();
        setFetchedDraft(null);
      } else if (channel === 'email') {
        result = await sendEmail({
          recipients: outbound, subject: subject.trim(), body,
          context: context ?? undefined,
          attachments: attachments.length ? attachments : undefined,
        }).unwrap();
      } else if (channel === 'sms') {
        result = await sendSms({ recipients: outbound, body, context: context ?? undefined }).unwrap();
      } else {
        result = await sendInApp({
          recipient_user_ids: recipients.map((r) => r.user_id).filter(Boolean) as string[],
          body, context: context ?? undefined,
        }).unwrap();
      }
      setComm(result);
      onSent?.(result);
    } catch (e) {
      setSendError(apiErrorMessage(e, t, t('communications.sendFailed', 'Failed to send message.')));
    }
  };

  const doDiscard = async () => {
    if (draftId) {
      try {
        await discardDraft(draftId).unwrap();
      } catch (e) {
        setSendError(apiErrorMessage(e, t, t('communications.discardFailed', 'Could not discard the draft.')));
        return;
      }
      setDiscarded(true);
    }
    onDiscard?.();
  };

  const doRetry = async () => {
    if (!comm) return;
    setSendError(null);
    try {
      setComm(await retryComm(comm.communication_id).unwrap());
    } catch (e) {
      setSendError(apiErrorMessage(e, t, t('communications.retryFailed', 'Retry failed.')));
    }
  };

  // ── attachments (email only) ─────────────────────────────────────────────────
  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSendError(null);
    try {
      const up = await uploadAttachment(file).unwrap();
      setAttachments((prev) => [...prev, up]);
    } catch (err) {
      setSendError(apiErrorMessage(err, t, t('communications.uploadFailed', 'Attachment upload failed.')));
    }
  };

  // ── display helpers ──────────────────────────────────────────────────────────
  const headerLabel = isDraft
    ? channel === 'email' ? t('communications.emailDraft', 'Email draft')
      : channel === 'sms' ? t('communications.smsDraft', 'SMS draft')
      : t('communications.inAppMessage', 'In-app message')
    : channel === 'email' ? t('communications.email', 'Email')
      : channel === 'sms' ? t('communications.sms', 'SMS')
      : t('communications.inAppMessage', 'In-app message');

  const eligibilityHint =
    channel === 'email' ? t('communications.hintEmail', 'Eligible for email: users, contacts, external addresses.')
    : channel === 'sms' ? t('communications.hintSms', 'Eligible for SMS: users, contacts, external phone numbers.')
    : t('communications.hintInApp', 'Eligible for in-app: workspace users only.');

  const sendLabel =
    channel === 'email' ? t('communications.sendEmail', 'Send email')
    : channel === 'sms' ? t('communications.sendSmsBtn', 'Send SMS')
    : t('communications.sendMessage', 'Send message');

  const isInbound = comm?.direction === 'inbound';
  const displayRecipients = comm
    ? (isInbound && comm.from_contact ? [comm.from_contact as RecipientIn] : comm.recipients)
    : recipients;

  const segments = Math.max(1, Math.ceil(body.length / SMS_SEGMENT));

  // ── full card ────────────────────────────────────────────────────────────────
  const fullCard = (
    <div className={cn('rounded-2xl border border-ui-border bg-ui-card shadow-sm overflow-hidden', className)}>
      {/* header strip */}
      <div className="flex items-center justify-between gap-2 border-b border-ui-border bg-ui-subtle px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <ChannelTag channel={channel} />
          <span className="truncate text-sm font-medium text-ui-text">{headerLabel}</span>
        </div>
        <StatusPill status={status} />
      </div>

      <div className="space-y-3 px-4 py-3">
        {/* context chip */}
        {context && (
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-ui-border bg-ui-subtle px-2.5 py-0.5 text-[11px] text-ui-text2">
              <span className="text-ui-text3">{t('communications.linkedTo', 'Linked to')}</span>
              {context.context_type}{context.display_label ? ` · ${context.display_label}` : ''}
            </span>
          </div>
        )}

        {/* recipients */}
        <div>
          <p className="mb-1.5 text-xs font-medium text-ui-text2">
            {isInbound ? t('communications.fromLabel', 'From')
              : channel === 'in_app' ? t('communications.recipients', 'Recipients') : t('communications.toLabel', 'To')}
          </p>
          <div className="flex flex-wrap items-start gap-1.5">
            {displayRecipients.map((r, i) => {
              const key = 'key' in r ? (r as DraftRecipient).key : `${r.name || r.email || r.phone || i}`;
              const contact = r.email || r.phone || '';
              return (
                <span key={key} className="inline-flex items-start gap-1.5 rounded-lg border border-ui-border bg-ui-subtle px-2 py-1">
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-ui-text">{r.name || contact || t('communications.recipient', 'Recipient')}</span>
                      <span className="rounded bg-ui-hover px-1 py-px text-[9px] font-medium uppercase tracking-wide text-ui-text3">
                        {(r.recipient_type || 'external').replace('_', ' ')}
                      </span>
                    </span>
                    {r.name && contact && <span className="block text-[11px] text-ui-text3">{contact}</span>}
                  </span>
                  {isDraft && (
                    <button
                      onClick={() => removeRecipient((r as DraftRecipient).key)}
                      aria-label={t('communications.removeRecipient', 'Remove recipient')}
                      className="mt-0.5 text-ui-text3 hover:text-ui-text"
                    >
                      ×
                    </button>
                  )}
                </span>
              );
            })}
            {isDraft && (
              <RecipientPickerInput channel={channel} onAddUser={addUser} onAddFreeText={addFreeText} excludeIds={userIds} />
            )}
          </div>
          {isDraft && <p className="mt-1.5 text-[11px] text-ui-text3">{eligibilityHint}</p>}
        </div>

        {/* validation banner */}
        {validationError && (
          <div className="rounded-lg border border-[#CF4148]/40 bg-red-50 px-3 py-2 text-xs text-[#CF4148] dark:bg-red-900/20">
            {validationError}
          </div>
        )}

        {/* subject — email only (SMS and in-app have no subject/title) */}
        {channel === 'email' && (
          <div>
            <p className="mb-1 text-xs font-medium text-ui-text2">
              {t('communications.subject', 'Subject')}
            </p>
            {isDraft ? (
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={500}
                className="h-9 w-full rounded-lg border border-ui-border bg-ui-input px-3 text-sm text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark"
              />
            ) : (
              <p className="text-sm font-medium text-ui-text">{comm?.subject || subject}</p>
            )}
          </div>
        )}

        {/* body */}
        <div>
          <p className="mb-1 text-xs font-medium text-ui-text2">{t('communications.message', 'Message')}</p>
          {isDraft ? (
            <>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={channel === 'sms' ? 3 : 6}
                className="w-full rounded-lg border border-ui-border bg-ui-input px-3 py-2 text-sm text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark"
              />
              {channel === 'sms' && (
                <p className="mt-0.5 text-right text-[11px] text-ui-text3">
                  {body.length}/{SMS_SEGMENT} · {segments} {segments === 1 ? t('communications.segment', 'segment') : t('communications.segments', 'segments')}
                </p>
              )}
            </>
          ) : (
            <div className="whitespace-pre-wrap rounded-lg border border-ui-border bg-ui-subtle px-3 py-2 text-sm text-ui-text">
              {comm?.body || body}
            </div>
          )}
        </div>

        {/* attachments (email only) */}
        {channel === 'email' && (isDraft || (comm?.attachments?.length ?? 0) > 0) && (
          <div>
            <p className="mb-1.5 text-xs font-medium text-ui-text2">{t('communications.attachments', 'Attachments')}</p>
            <div className="flex flex-wrap items-center gap-1.5">
              {(comm ? comm.attachments : attachments).map((a) => (
                <span key={a.url} className="inline-flex items-center gap-1.5 rounded-lg border border-ui-border bg-ui-subtle px-2 py-1">
                  <span className="rounded bg-ui-hover px-1 py-px text-[9px] font-semibold uppercase text-ui-text3">
                    {a.file_name.split('.').pop() || 'file'}
                  </span>
                  <span className="max-w-[160px] truncate text-xs text-ui-text">{a.file_name}</span>
                  {a.size_bytes != null && <span className="text-[10px] text-ui-text3">{formatBytes(a.size_bytes)}</span>}
                  {isDraft && (
                    <button
                      onClick={() => setAttachments((prev) => prev.filter((x) => x.url !== a.url))}
                      aria-label={t('communications.removeAttachment', 'Remove attachment')}
                      className="text-ui-text3 hover:text-ui-text"
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
              {isDraft && (
                <>
                  <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} />
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-dashed border-ui-border px-2.5 text-xs text-ui-text2 hover:bg-ui-hover disabled:opacity-50"
                  >
                    {uploading ? <Spinner size="sm" className="h-3.5 w-3.5" /> : <span aria-hidden>📎</span>}
                    {uploading ? t('communications.uploading', 'Uploading…') : t('communications.attachFile', 'Attach file')}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* send error */}
        {sendError && (
          <div className="rounded-lg border border-[#CF4148]/40 bg-red-50 px-3 py-2 text-xs text-[#CF4148] dark:bg-red-900/20">
            {sendError}
          </div>
        )}

        {/* failed banner + retry */}
        {comm && status === 'failed' && (
          <div className="flex items-start justify-between gap-3 rounded-lg border border-[#CF4148]/40 bg-red-50 px-3 py-2 dark:bg-red-900/20">
            <p className="text-xs text-[#CF4148]">
              {comm.error_detail || t('communications.deliveryFailed', 'Message delivery failed.')}
            </p>
            <Button variant="outline" size="sm" loading={retrying} onClick={doRetry} className="shrink-0">
              ↻ {t('communications.retry', 'Retry')}
            </Button>
          </div>
        )}
      </div>

      {/* footer while draft */}
      {isDraft && (
        <div className="flex items-center justify-between gap-2 border-t border-ui-border px-4 py-2.5">
          <span className="text-[11px] text-ui-text3">{t('communications.reviewBeforeSending', 'Review before sending')}</span>
          <div className="flex items-center gap-2">
            {(onDiscard || draftId) && (
              <Button variant="ghost" size="sm" loading={discarding} onClick={doDiscard}>
                {t('communications.discard', 'Discard')}
              </Button>
            )}
            <Button size="sm" loading={sending} onClick={doSend}>{sendLabel}</Button>
          </div>
        </div>
      )}
    </div>
  );

  // ── discarded / loading states for persisted drafts ──────────────────────────
  if (discarded) {
    return (
      <p className={cn('rounded-xl border border-dashed border-ui-border px-3 py-2 text-xs italic text-ui-text3', className)}>
        {t('communications.draftDiscarded', 'Draft discarded.')}
      </p>
    );
  }
  if (communicationId && !communication && !comm && !fetchedDraft && !draft) {
    if (loadingComm) {
      return (
        <div className={cn('flex items-center gap-2 rounded-2xl border border-ui-border bg-ui-card px-4 py-3', className)}>
          <Spinner size="sm" />
          <span className="text-xs text-ui-text3">{t('communications.loadingDraft', 'Loading message…')}</span>
        </div>
      );
    }
    if (!draft) {
      return (
        <p className={cn('rounded-xl border border-dashed border-ui-border px-3 py-2 text-xs italic text-ui-text3', className)}>
          {t('communications.messageUnavailable', 'Message unavailable.')}
        </p>
      );
    }
  }

  return fullCard;
};

export default ComposeCard;
